/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/string/CapitalizeSentences.ts
 * @create:      2025-06-26 10:43:26.943
 * @modify:      2025-07-11 21:19:12.858
 * @version:     6.0.0
 * @times:       8
 * @lines:       63
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Capitalizes the first letter of each sentence in the given string.
 * Sentences are determined by the delimiters: period (.), exclamation mark (!), and question mark (?).
 * Each sentence will have its first character capitalized and the rest converted to lowercase.
 * Leading spaces after sentence delimiters are trimmed.
 *
 * @param sentences - The input string containing one or more sentences.
 * @returns A new string with each sentence capitalized appropriately.
 * 
 * @since 4.0.0
 * @version 2025-06-26
 */
export function CapitalizeSentences(sentences: string): string {
    // Regex to match sentence endings: . ! ? followed by space(s) or end of string
    return sentences.replace(/([^.!?]*[.!?]*)/g, (match) => {
        // Trim leading spaces
        const trimmed = match.replace(/^\s+/, "");
        if (!trimmed) return match; // preserve original if empty
        // Find first letter
        const firstLetterIndex = trimmed.search(/[a-zA-Z]/);
        if (firstLetterIndex === -1) return match; // no letter, return as is
        // Capitalize first letter, keep rest as is
        return (
            match.slice(0, match.indexOf(trimmed)) + // preserve leading spaces
            trimmed.slice(0, firstLetterIndex) +
            trimmed.charAt(firstLetterIndex).toUpperCase() +
            trimmed.slice(firstLetterIndex + 1)
        );
    });
}
